import { Injectable, inject } from "@angular/core";
import { Avatar } from "../../models/avatar-models/Avatar";
import { Observable, from } from "rxjs";
import { AvatarBuilderData } from "../../models/avatar-models/AvatarBuilderData";
import { BaseService } from "../base.service";
import { HttpRestService } from "../http-client/http-rest-client.service";
import { AvatarOutfitSpecificationGenericRequest } from "../../models/avatar-models/AvatarOutfitSpecificationGenericRequest";
import { EditAiAvatarModel } from "../../models/avatar-models/EditAiAvatarModel";
import { SaveAvatarModel } from "../../models/avatar-models/SaveAvatarModel";
import { SaveAvatarGenericSpecificationModel } from "../../models/avatar-models/SaveAvatarGenericSpecificationModel";
import { EditAiAvatarGenericModel } from "../../models/avatar-models/EditAiAvatarGenericModel";
import { SaveAvatarGenericModel } from "../../models/avatar-models/SaveAvatarGenericModel";
import { AvatarTypes } from "../../enums/avatar/AvatarTypes";

@Injectable()
export class AvatarApiService extends BaseService {
    private restService = inject(HttpRestService);


    public getAllNonDealIdAvatars(): Observable<Avatar[]> {
        return from(this.handleResponse(this.restService.get({
            url: `/avatar/get-all-non-deal-id`
        })));
    }

    public getAvatar(avatarId: number): Observable<Avatar> {
        return from(this.handleResponse(this.restService.get({
            url: `/avatar/get/${avatarId}`
        })));
    }

    public getSeasonalProposal(): Observable<Avatar[]> {
        return from(this.handleResponse(this.restService.get({
            url: `/avatar/get-seasonal-proposal`
        })));
    }

    public getAvatarsBase64(avatarIds: number[]): Observable<Avatar[]> {
        const data =
        {
            avatarIds: avatarIds
        }
        return from(this.handleResponse(this.restService.post({
            url: `/avatar/get-avatars-base64`,
            data: data
        })));
    }

    public createAvatar(avatarBuilderData: AvatarBuilderData): Observable<number> {
        const data = avatarBuilderData;
        return from(this.handleResponse(this.restService.post({
            url: `/avatar/create-avatar`,
            data: data
        })));
    }

    public createAvatarDuplicate(avatarId: number, dealId: number = null, clientCardId: number = null): Observable<number> {
        const data = {
            avatarId: avatarId,
            dealId: dealId,
            clientCardId: clientCardId
        }
        return from(this.handleResponse(this.restService.post({
            url: `/avatar/create-avatar-duplicate`,
            data: data
        })));
    }

    public updateAvatar(avatarId: number, avatarBuilderData: AvatarBuilderData): Observable<boolean> {
        const data = avatarBuilderData;
        return from(this.handleResponse(this.restService.put({
            url: `/avatar/update-avatar/${avatarId}`,
            data: data
        })));
    }

    public deleteAvatar(avatarId: number): Observable<boolean> {
        return from(this.handleResponse(this.restService.delete({
            url: `/avatar/delete-avatar/${avatarId}`
        })));
    }

    public downloadAvatar(avatarId: number): Observable<string> {
        return from(this.handleResponse(this.restService.get({
            url: `/avatar/download-avatar/${avatarId}`
        })));
    }

    public downloadAllNonDealId(): Observable<Blob> {
        return from(this.handleResponse(this.restService.get({
            url: `/avatar/download-all-non-deal-id`,
            responseType: 'blob' as 'json'
        })));
    }

    public massAvatarAiGeneration(dealId: number, onlyWithoutAi: boolean = false, useDefaultMeasurementsIfNeeded: boolean = true): Promise<any> {
        return this.handleResponse(this.restService.post({
            url: '/avatar/mass-avatar-ai-generation',
            data: { dealId, onlyWithoutAi, useDefaultMeasurementsIfNeeded }
        }));
    }

    public massAvatarAiGenerationGeneric(collectionId: number | null, onlyWithoutAi: boolean = false): Promise<any> {
        return this.handleResponse(this.restService.post({
            url: '/avatar/mass-avatar-ai-generation-generic',
            data: { collectionId, onlyWithoutAi, useDefaultMeasurementsIfNeeded: true }
        }));
    }

    public getAvatarOutfitSpecification(data: AvatarOutfitSpecificationGenericRequest): Promise<string> {
        return this.handleResponse(this.restService.post({
            url: '/avatar/get-avatar-outfit-specification',
            data: data
        }));
    }

    public getAiPromptWithDefaultMeasurements(avatarTypeId: AvatarTypes): Promise<string> {
        return this.handleResponse(this.restService.get({
            url: `/avatar/get-ai-prompt-with-default-measurements?avatarTypeId=${avatarTypeId}`
        }));
    }

    public getAiPromptWithClientMeasurements(dealId: number, useDefaultMeasurementsIfNeeded: boolean, avatarTypeId: AvatarTypes): Promise<string> {
        return this.handleResponse(this.restService.get({
            url: `/avatar/get-ai-prompt-with-client-measurements/${dealId}?useDefaultMeasurementsIfNeeded=${useDefaultMeasurementsIfNeeded}&avatarTypeId=${avatarTypeId}`
        }));
    }

    public getEditAvatarByAi(data: EditAiAvatarModel): Promise<{ imageBase64: string; fullPrompt: string }> {
        return this.handleResponse(this.restService.post({
            url: '/avatar/get-edit-avatar-by-ai',
            data: data
        }));
    }

    public getEditAvatarByAiGeneric(data: EditAiAvatarGenericModel): Promise<{ imageBase64: string; fullPrompt: string }> {
        return this.handleResponse(this.restService.post({
            url: '/avatar/get-edit-avatar-by-ai-generic',
            data: data
        }));
    }

    public getAiGeneratedAvatarPrompt(avatarId: number, dealId?: number): Promise<string> {
        return this.handleResponse(this.restService.post({
            url: `/avatar/get-ai-generated-avatar-prompt`,
            data: { avatarId, dealId }
        }));
    }

    public getAiGeneratedPromptForCustomCard(customCardId: number, dealId?: number): Promise<string> {
        const dealParam = dealId === undefined || dealId === null ? '' : `&dealId=${dealId}`;
        return this.handleResponse(this.restService.get({
            url: `/avatar/get-ai-generated-prompt-for-custom-card?customCardId=${customCardId}${dealParam}`
        }));
    }

    public saveAvatarGenericSpecification(model: SaveAvatarGenericSpecificationModel): Promise<any> {
        return this.handleResponse(this.restService.post({
            url: '/avatar/save-avatar-generic-specification',
            data: model
        }));
    }

    public saveAiAvatar(model: SaveAvatarModel): Promise<any> {
        return this.handleResponse(this.restService.post({
            url: '/avatar/save-ai-avatar',
            data: model
        }));
    }

    public saveAiAvatarGeneric(model: SaveAvatarGenericModel): Promise<any> {
        return this.handleResponse(this.restService.post({
            url: '/avatar/save-ai-avatar-generic',
            data: model
        }));
    }

    public getCategoryTypesMap(): Promise<Record<string, number[]>> {
        return this.handleResponse(this.restService.get({
            url: `/avatar/category-types-map`
        }));
    }

    public getAvatarTypeLabels(): Promise<Record<string, string>> {
        return this.handleResponse(this.restService.get({
            url: `/avatar/avatar-type-labels`
        }));
    }

    public getDefaultPromptsGrouped(): Promise<{ typeIds: number[]; prompt: string }[]> {
        return this.handleResponse(this.restService.get({
            url: `/avatar/default-prompts-grouped`
        }));
    }
}
